import React from 'react';
import { format, set, isValid } from 'date-fns';
import { Clock, Minus, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { parseTypedTime } from './dateUtils';

const QUICK = [
  { label: 'Now', now: true },
  { label: '9:00 AM', h: 9, m: 0 },
  { label: '12:00 PM', h: 12, m: 0 },
  { label: '5:00 PM', h: 17, m: 0 },
];

const TYPE_HINT = 'Type a time like 9:30 am, 930, 0930 or 17:30';

// Right-hand time controls: big field, Hour/Minute steppers, quick selects.
export function TimePanel({ date, onChange, minuteStep = 5, showQuick = true, testIdPrefix = 'tdt', onTextInvalid }) {
  const working = date && isValid(date) ? date : null;
  const workingKey = working ? working.getTime() : 'none';
  const [text, setText] = React.useState(working ? format(working, 'hh:mm a') : '');
  const [hint, setHint] = React.useState('');
  // BS3-006: a change that came from typing must not rewrite the text the user is still typing
  const fromTyping = React.useRef(false);

  React.useEffect(() => {
    if (fromTyping.current) { fromTyping.current = false; return; }
    setText(working ? format(working, 'hh:mm a') : '');
    setHint('');
    onTextInvalid && onTextInvalid(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workingKey]);

  const base = () => working || set(new Date(), { seconds: 0, milliseconds: 0 });
  const emit = (d) => onChange(set(d, { seconds: 0, milliseconds: 0 }));

  const stepHour = (delta) => {
    const d = base();
    emit(set(d, { hours: (d.getHours() + delta + 24) % 24 }));
  };
  const stepMinute = (delta) => {
    const d = base();
    const total = d.getHours() * 60 + d.getMinutes() + delta * minuteStep;
    const norm = ((total % 1440) + 1440) % 1440;
    emit(set(d, { hours: Math.floor(norm / 60), minutes: norm % 60 }));
  };
  // While typing: every readable value goes straight to the steppers / summary (and Apply); the text is left alone.
  const onType = (v) => {
    setText(v);
    const p = parseTypedTime(v);
    onTextInvalid && onTextInvalid(!p && !!String(v).trim());   // Apply waits until the typed time can be read
    if (p) {
      setHint('');
      if (!working || working.getHours() !== p.h || working.getMinutes() !== p.m) {
        fromTyping.current = true;
        emit(set(base(), { hours: p.h, minutes: p.m }));
      }
    }
  };
  // Leaving the field / Enter: tidy a readable time to "09:30 AM"; an unreadable one stays as typed with a hint
  // (it used to jump back to the old time without a word).
  const commitText = (v) => {
    if (!String(v || '').trim()) { setText(working ? format(working, 'hh:mm a') : ''); setHint(''); onTextInvalid && onTextInvalid(false); return; }
    const p = parseTypedTime(v);
    if (!p) { setHint(TYPE_HINT); onTextInvalid && onTextInvalid(true); return; }
    setHint('');
    onTextInvalid && onTextInvalid(false);
    const next = set(base(), { hours: p.h, minutes: p.m, seconds: 0, milliseconds: 0 });
    setText(format(next, 'hh:mm a'));
    if (!working || working.getHours() !== p.h || working.getMinutes() !== p.m) {
      fromTyping.current = true;
      emit(next);
    }
  };
  const setQuick = (q) => {
    if (q.now) {
      const n = new Date();
      emit(set(base(), { hours: n.getHours(), minutes: n.getMinutes() }));
    } else {
      emit(set(base(), { hours: q.h, minutes: q.m }));
    }
  };

  const hourLabel = working ? format(working, 'hh') : '--';
  const minLabel = working ? format(working, 'mm') : '--';

  return (
    <div className="space-y-3 max-sm:space-y-5" data-testid={`${testIdPrefix}-time-panel`}>
      <div>
        <div className="text-sm font-medium text-muted-foreground mb-2">Time</div>
        <div className="relative">
          <Input
            value={text}
            onChange={(e) => onType(e.target.value)}
            onBlur={(e) => commitText(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); commitText(e.currentTarget.value); } }}
            placeholder="--:-- --"
            aria-label="Time"
            aria-invalid={hint ? true : undefined}
            aria-describedby={hint ? `${testIdPrefix}-time-hint` : undefined}
            inputMode="text"
            autoComplete="off"
            data-testid={`${testIdPrefix}-time-input`}
            className={cn('h-11 text-xl max-sm:h-16 max-sm:text-3xl font-semibold tracking-wide pr-12 focus-visible:ring-violet-500 focus-visible:border-violet-500',
              hint && 'border-amber-500')}
          />
          <Clock className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 max-sm:w-6 max-sm:h-6 text-muted-foreground" />
        </div>
        {hint && <p className="mt-1 text-xs text-amber-700" id={`${testIdPrefix}-time-hint`} role="status" data-testid={`${testIdPrefix}-time-hint`}>{hint}</p>}
      </div>

      <StepperRow
        label="Hour"
        value={hourLabel}
        onDec={() => stepHour(-1)}
        onInc={() => stepHour(1)}
        testId={`${testIdPrefix}-hour`}
      />
      <StepperRow
        label="Minute"
        value={minLabel}
        onDec={() => stepMinute(-1)}
        onInc={() => stepMinute(1)}
        testId={`${testIdPrefix}-minute`}
      />

      {showQuick && (
        <div>
          <div className="text-sm font-medium text-muted-foreground mb-2">Quick select</div>
          <div className="flex flex-wrap gap-2">
            {QUICK.map((q) => (
              <button
                key={q.label}
                type="button"
                onClick={() => setQuick(q)}
                data-testid={`${testIdPrefix}-quick-${q.label.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}`}
                className="px-3 py-1 max-sm:min-h-[44px] max-sm:py-2.5 max-sm:px-4 rounded-md text-sm font-medium bg-violet-50 text-violet-700 border border-violet-200 hover:bg-violet-100 transition-colors"
              >
                {q.label}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function StepperRow({ label, value, onDec, onInc, testId }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-sm font-medium">{label}</span>
      <div className="flex items-center gap-3">
        <Button type="button" variant="outline" size="icon" className="h-8 w-8 max-sm:h-11 max-sm:w-11" onClick={onDec}
          aria-label={`Decrease ${label.toLowerCase()}`} data-testid={`${testId}-dec`}>
          <Minus className="w-4 h-4" />
        </Button>
        <span className="w-8 text-center text-lg font-semibold tabular-nums" data-testid={`${testId}-value`}>{value}</span>
        <Button type="button" variant="outline" size="icon" className="h-8 w-8 max-sm:h-11 max-sm:w-11" onClick={onInc}
          aria-label={`Increase ${label.toLowerCase()}`} data-testid={`${testId}-inc`}>
          <Plus className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
}
